import classNames from 'clsx';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MemoryRouter, Route, matchPath } from 'react-router';

import RedirectNotification, {
  localStorageKey as redirectNotificationKey
} from '@swf/components/RedirectNotification/RedirectNotification';
import StarsModalRoutes from '@swf/components/StarsModalRoutes/StarsModalRoutes';
import type { Modal } from '@swf/core/models/modal';
import { Size } from '@swf/core/models/modal';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { ClassNameProp } from '@swf/core/types/Props';
import { canUseLocalStorage } from '@swf/core/utils';

import { ModalContext } from '../StarsModal/Context';

import BaseModal from './BaseModal';
import styles from './StarsModal.scss';

export interface State extends ClassNameProp {
  size: Size;
  hidden?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  overlayClassName?: string;
}

const defaultState: State = {
  size: Size.SMALL,
  hidden: false,
  closeOnOverlayClick: undefined,
  closeOnEsc: undefined,
  overlayClassName: '',
  className: ''
};

const StarsModal: React.FC = () => {
  const [{ className, overlayClassName, hidden, size, closeOnOverlayClick, closeOnEsc }, setState] =
    useState<State>(defaultState);
  const { path, locationState } = (useSelector(select.modal.getCurrent) || {}) as Partial<Modal>;
  const { modal } = useDispatch<Dispatch>();
  const { redirect } = useSelector(select.page.getMeta);

  const setStyle = useCallback(
    (newState: Partial<State> = {}) => setState({ ...defaultState, ...newState }),
    []
  );

  useEffect(() => {
    if (!path) setState(defaultState);
  }, [path]);

  const isRedirect =
    !!redirect?.statusCode &&
    !(canUseLocalStorage && localStorage.getItem(redirectNotificationKey));

  return useMemo(
    () => (
      <MemoryRouter
        keyLength={process.env.NODE_ENV === 'test' ? 0 : 6}
        initialEntries={[{ pathname: path, state: locationState }]}
      >
        <Route
          render={({ location: { pathname } }) => (
            <BaseModal
              key={pathname ? pathname.split('/')[0] : undefined}
              className={classNames(styles.modal, className)}
              overlayClassName={overlayClassName}
              isOpen={!!path || isRedirect}
              onRequestClose={() => modal.close()}
              size={size}
              hidden={hidden}
              fullScreen={!!matchPath('/casino/player-selector', pathname)}
              showAnimation={!hidden}
              closeOnOverlayClick={closeOnOverlayClick}
              closeOnEsc={closeOnEsc}
            >
              {isRedirect ? (
                <ModalContext.Provider value={{ setStyle }}>
                  <RedirectNotification />
                </ModalContext.Provider>
              ) : (
                <StarsModalRoutes setStyle={setStyle} />
              )}
            </BaseModal>
          )}
        />
      </MemoryRouter>
    ),
    [
      path,
      locationState,
      className,
      overlayClassName,
      size,
      hidden,
      closeOnOverlayClick,
      closeOnEsc,
      setStyle,
      modal,
      isRedirect
    ]
  );
};

export default StarsModal;
